import { AUTH_LEARNER_CREATED } from "@outschool/auth-shared";
import { ChildFragmentFragment } from "@outschool/gql-frontend-generated";
import { useLocalStorageState } from "@outschool/local-storage";
import { useTranslation } from "@outschool/localization";
import { pathnameWithoutLocale, searchPath } from "@outschool/routes";
import { useSession } from "@outschool/ui-auth";
import Cookies from "js-cookie";
import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect
} from "react";
import { useLocation } from "react-router-dom";

import { useActiveLearner } from "./ActiveLearnerProvider";

const LOCAL_STORAGE_KEY = "searchByLearnerUid";

interface SearchByLearnerContextValue {
  showLearnerFilter: boolean;
  learner: ChildFragmentFragment | null;
  setLearner: (learner: ChildFragmentFragment | null) => void;
  searchBarPlaceholder: string;
  getSearchBarPlaceholder: (learner: ChildFragmentFragment | null) => string;
}

const SearchByLearnerContext = createContext<SearchByLearnerContextValue>({
  showLearnerFilter: false,
  learner: null,
  setLearner: () => {},
  searchBarPlaceholder: "",
  getSearchBarPlaceholder: () => ""
});

interface SearchByLearnerProviderProps {
  children: ReactNode;
}

export function SearchByLearnerProvider({
  children
}: SearchByLearnerProviderProps) {
  const { setActiveLearner } = useActiveLearner();
  const [learnerUid, saveLearnerUid] = useLocalStorageState<string | null>(
    LOCAL_STORAGE_KEY,
    null
  );
  const { currentUser } = useSession();
  const { t } = useTranslation("client\\providers\\SearchByLearnerProvider");
  const location = useLocation();

  const showLearnerFilter = Boolean(currentUser?.children.length);

  useEffect(() => {
    const currentPath = pathnameWithoutLocale(location.pathname);

    // Reset the learner uid when not on the search page
    if (currentPath !== searchPath()) {
      saveLearnerUid(null);
    }
  }, [location.pathname, saveLearnerUid]);

  const setLearner = useCallback(
    (learner: ChildFragmentFragment | null) => {
      saveLearnerUid(learner?.uid ?? null);
    },
    [saveLearnerUid]
  );

  const learner =
    currentUser?.children.find(child => child?.uid === learnerUid) ?? null;

  const getSearchBarPlaceholder = React.useCallback(
    (learner: ChildFragmentFragment | null) => {
      if (!currentUser) {
        return t("Search online classes and tutors");
      }

      if (currentUser.children.length > 0 && learner) {
        return t("Search classes for {{learnerName}}", {
          learnerName: learner.name
        });
      }

      return t("Search all Outschool classes");
    },
    [currentUser, t]
  );

  const searchBarPlaceholder = getSearchBarPlaceholder(learner);

  // Learner is created with signing up during onboarding
  useEffect(() => {
    const isLearnerCreated = Cookies.get(AUTH_LEARNER_CREATED);
    if (isLearnerCreated) {
      const createdLearner = currentUser?.children.at(0);
      if (createdLearner) {
        setLearner(createdLearner);
        setActiveLearner(createdLearner.uid);
      }
      Cookies.remove(AUTH_LEARNER_CREATED);
    }
  }, [setLearner, setActiveLearner, currentUser?.children]);

  return (
    <SearchByLearnerContext.Provider
      value={{
        showLearnerFilter,
        learner,
        setLearner,
        searchBarPlaceholder,
        getSearchBarPlaceholder
      }}
    >
      {children}
    </SearchByLearnerContext.Provider>
  );
}

export function useSearchByLearner() {
  const context = useContext(SearchByLearnerContext);

  if (context === undefined) {
    throw new Error(
      "useSearchByLearner must be used within an SearchByLearnerProvider"
    );
  }

  return context;
}
